import React, { useState } from "react";
import { adminApi, formatApiError } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";

/**
 * Bevestiging naar de klant mailen voor een pickup- én delivery-afspraak.
 *
 * Jun 2026 hotfix — voorheen was de flow pickup-only in tekst en PDF, waardoor
 * een delivery-afspraak niet zinnig naar de klant te versturen was. Nu is de
 * soort (ophalen / afleveren / beide) expliciet en gaat die mee naar dezelfde
 * bestaande endpoint, mailservice, PDF, email_log en RBAC.
 */
const LEG_FIELDS = ["date", "time", "address", "street", "city", "postal_code", "instructions"];

export function resolveAppointmentKind(appointment) {
  const has = (prefix) =>
    LEG_FIELDS.some((f) => String(appointment?.[`${prefix}_${f}`] || "").trim());
  const hasPickup = has("pickup");
  const hasDelivery = has("delivery");
  if (hasPickup && hasDelivery) return "both";
  if (hasDelivery) return "delivery";
  if (hasPickup) return "pickup";
  return "both";
}

const KIND_LABELS = {
  pickup: "Ophaalbevestiging",
  delivery: "Afleverbevestiging",
  both: "Ophaal- en afleverbevestiging",
};

export default function SendPickupConfirmationButton({
  pickupId, defaultEmail = "", appointment = null,
}) {
  const derivedKind = resolveAppointmentKind(appointment || {});
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [kind, setKind] = useState(derivedKind);
  const [sending, setSending] = useState(false);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      setEmail(defaultEmail || "");
      setKind(derivedKind);
    }
  };

  const send = async () => {
    if (!email.trim()) {
      toast.error("Voer een geldig e-mailadres in.");
      return;
    }
    setSending(true);
    try {
      const r = await adminApi.post(`/admin/pickup/${pickupId}/send-confirmation`, {
        recipient_email: email.trim(),
        kind,
      });
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(`${KIND_LABELS[kind]} verzonden.`);
      } else if (status === "skipped_no_key") {
        toast.message("RESEND_API_KEY ontbreekt — bericht niet daadwerkelijk verzonden, wel gelogd.");
      } else if (status === "failed") {
        toast.error(`Verzenden mislukt: ${r.data?.error || "onbekende fout"}`);
      } else {
        toast.success("Verzonden.");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-none border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white h-12 font-chivo font-bold uppercase text-xs"
          data-testid="pickup-send-confirmation-button"
        >
          <Mail className="w-4 h-4 mr-1" /> Bevestiging mailen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bevestiging naar klant mailen</DialogTitle>
          <DialogDescription>
            Stuur een e-mailbevestiging met de afspraakdetails (adres, datum, tijd)
            en de bijbehorende PDF naar de klant.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Soort bevestiging</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="pickup-confirmation-kind-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pickup">Ophalen</SelectItem>
                <SelectItem value="delivery">Afleveren</SelectItem>
                <SelectItem value="both">Ophalen + afleveren</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500 mt-1" data-testid="pickup-confirmation-kind-hint">
              Voorgesteld op basis van deze afspraak: {KIND_LABELS[derivedKind]}.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Ontvanger</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="klant@voorbeeld.nl"
              className="rounded-none mt-1"
              data-testid="pickup-confirmation-email-input"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 mt-3">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-none text-xs"
            data-testid="pickup-confirmation-cancel"
          >
            Annuleren
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none text-xs font-chivo font-bold uppercase"
            data-testid="pickup-confirmation-send"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            Verzenden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
